/**
 * Device settings in `localStorage['marg.settings.v1']` (GDD §4.15, C11): NOT part of the save, never exported.
 * The stored shape is F3's `UiSettings` (the full ui-ux §6.24 row set, a superset of the frozen `DeviceSettings`), so the
 * Settings screen (`createSettingsScreen(portOf)`) talks to this store through {@link SettingsStore.port}.
 *
 * URL overrides (`motion=`, `q=`, `textscale=`, `lefthand=`, `font=`) apply to the session only and are never
 * persisted. Effective values are mirrored on `<html>` by F3's `applySettingsToRoot`.
 */
import { STORAGE_KEYS, signal, type QualityTier, type ReadonlySignal, type Signal } from '../shared';
import { DEFAULT_UI_SETTINGS, applySettingsToRoot, sanitizeUiSettings, type SettingsPort, type UiSettings } from '../ui';
import type { KeyValueStorage } from './save';

export { DEFAULT_UI_SETTINGS as DEFAULT_SETTINGS, sanitizeUiSettings as sanitizeSettings, type UiSettings };

export interface SessionOverrides {
  motion?: UiSettings['motion'] | null;
  textScale?: UiSettings['textScale'] | null;
  quality?: QualityTier | null;
  leftHanded?: boolean | null;
  font?: UiSettings['font'] | null;
}

export class SettingsStore {
  private readonly persisted: Signal<UiSettings>;
  private readonly eff: Signal<UiSettings>;

  constructor(private readonly storage: KeyValueStorage | null, private readonly overrides: SessionOverrides, systemReducedMotion: boolean) {
    let stored: unknown = null;
    const raw = storage?.getItem(STORAGE_KEYS.settings) ?? null;
    if (raw) { try { stored = JSON.parse(raw); } catch { stored = null; } }
    const base: UiSettings = { ...DEFAULT_UI_SETTINGS, motion: systemReducedMotion ? 'reduced' : 'full', shake: !systemReducedMotion, flash: !systemReducedMotion };
    this.persisted = signal(sanitizeUiSettings(stored, base));
    this.eff = signal(this.withOverrides(this.persisted.get()));
  }

  /** Effective settings (persisted + session overrides). */
  get value(): UiSettings { return this.eff.get(); }
  get signal(): ReadonlySignal<UiSettings> { return this.eff; }
  /** What is stored on this device. */
  get stored(): UiSettings { return this.persisted.get(); }

  /** Changes and persists settings (Settings screen, `__marg.setQuality`). Never touches `marg.save.*`. A changed key
   *  drops its session override (the player's explicit choice wins over the URL). */
  set(patch: Partial<UiSettings>): UiSettings {
    const next = sanitizeUiSettings({ ...this.persisted.get(), ...patch }, this.persisted.get());
    this.persisted.set(next);
    try { this.storage?.setItem(STORAGE_KEYS.settings, JSON.stringify(next)); } catch (e) { console.warn('[settings] not saved', e); }
    for (const k of Object.keys(patch) as Array<keyof SessionOverrides>) if (k in this.overrides) this.overrides[k] = null;
    this.eff.set(this.withOverrides(next));
    return this.eff.get();
  }

  /** The F3 `SettingsPort` (the Settings screen reads/writes through it). */
  port(extra: Pick<SettingsPort, 'env' | 'actions'> = {}): SettingsPort {
    return { get: () => this.value, set: (p) => { this.set(p); }, signal: this.eff, ...extra };
  }

  private withOverrides(s: UiSettings): UiSettings {
    const o = this.overrides;
    return {
      ...s,
      motion: o.motion ?? s.motion,
      textScale: o.textScale ?? s.textScale,
      quality: o.quality ?? s.quality,
      leftHanded: o.leftHanded ?? s.leftHanded,
      font: o.font ?? s.font,
    };
  }
}

/** Mirrors effective settings on `<html>` (motion level, text scale, font, left hand, contrast; day theme). */
export function applyRootSettings(root: HTMLElement, s: UiSettings): void {
  applySettingsToRoot(root, s);
}
